import axios from 'axios';
import { API_URL, TOKEN_EXPIRATION } from '../config';
import { processApiError, ErrorTypes } from '../utils/errorHandler';
import { debug, error } from '../utils/logger';

// Constantes para el almacenamiento local
const TOKEN_KEY = 'prokids_token';
const REFRESH_TOKEN_KEY = 'prokids_refresh_token';
const USER_KEY = 'prokids_user';
const TOKEN_EXPIRY_KEY = 'prokids_token_expiry';
const LAST_ACTIVITY_KEY = 'prokids_last_activity';

// Obtener la URL base de la API desde las variables de entorno o usar un valor por defecto
const API_BASE_URL = process.env.REACT_APP_API_URL || API_URL;
debug('API_BASE_URL inicializada', { apiBaseUrl: API_BASE_URL });

// Variable para controlar si hay una renovación de token en curso
let isRefreshing = false;
// Cola de peticiones pendientes durante la renovación del token
let refreshSubscribers = [];

// Función para suscribir peticiones a la renovación del token
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Función para notificar a los suscriptores cuando el token se ha renovado
const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Función para renovar el token
export const refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      throw new Error('No hay token de refresco disponible');
    }

    // Usar una instancia temporal de axios para evitar ciclos de dependencia
    const response = await axios.post(
      `${API_BASE_URL}/auth/refresh-token`,
      { refreshToken },
      { withCredentials: true },
    );

    if (response.data && response.data.token) {
      // Guardar el nuevo token y su tiempo de expiración
      localStorage.setItem(TOKEN_KEY, response.data.token);
      const expiryTime = new Date().getTime() + TOKEN_EXPIRATION;
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

      // Guardar el nuevo token de refresco si está disponible
      if (response.data.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
      }

      // Actualizar la última actividad
      localStorage.setItem(LAST_ACTIVITY_KEY, new Date().getTime().toString());

      // Notificar a los suscriptores
      onTokenRefreshed(response.data.token);

      return response.data.token;
    }

    throw new Error('No se pudo renovar el token');
  } catch (refreshError) {
    error('Error al renovar el token', { error: refreshError.message });
    // Limpiar datos de autenticación
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);

    // Redirigir al login
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login?session=expired';
    }

    throw error;
  } finally {
    isRefreshing = false;
  }
};

// Función para verificar si el token ha expirado
export const isTokenExpired = () => {
  const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryTime) return true;

  return new Date().getTime() > parseInt(expiryTime);
};

// Función para actualizar la última actividad del usuario
export const updateLastActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, new Date().getTime().toString());
};

// Crear una instancia de axios con la URL base
const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 segundos de timeout
  withCredentials: true, // Importante para solicitudes cross-origin con credenciales
});

// Interceptor para añadir el token a las peticiones y renovarlo si es necesario
API.interceptors.request.use(
  async (config) => {
    // Actualizar la última actividad del usuario
    updateLastActivity();

    // Obtener el token
    let token = localStorage.getItem(TOKEN_KEY);

    debug('Estado del token', {
      present: !!token,
      expired: token && isTokenExpired()
    });

    // Si hay un token y ha expirado, intentar renovarlo
    if (token && isTokenExpired()) {
      debug('Intentando renovar token expirado');
      // Si ya hay una renovación en curso, esperar a que termine
      if (isRefreshing) {
        debug('Renovación ya en curso, esperando');
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            if (newToken) {
              config.headers.Authorization = `Bearer ${newToken}`;
              debug('Token renovado por otra petición');
            }
            resolve(config);
          });
        });
      }

      // Iniciar renovación del token
      debug('Iniciando renovación de token');
      isRefreshing = true;
      try {
        token = await refreshToken();
        debug('Token renovado exitosamente');
      } catch (refreshError) {
        error('Error al renovar token', { error: refreshError.message });
        // Si falla la renovación, limpiar el token y continuar
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        return config;
      }
    }

    // Añadir el token a la cabecera si está disponible
    if (token) {
      debug('Añadiendo token a la petición');
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      debug('Petición sin token de autenticación');
    }

    debug('Configuración de la petición', {
      url: config.url,
      method: config.method,
      hasAuthHeader: !!config.headers.Authorization,
    });

    return config;
  },
  (requestError) => {
    error('Error en interceptor de petición', { error: requestError.message });
    return Promise.reject(requestError);
  },
);

// Interceptor para manejar errores de respuesta
API.interceptors.response.use(
  (response) => {
    console.log('Respuesta exitosa:', {
      url: response.config.url,
      status: response.status,
      data: response.data ? 'Datos presentes' : 'Sin datos',
    });

    return response;
  },
  async (error) => {
    console.error('Error en respuesta:', {
      url: error.config && error.config.url,
      status: error.response && error.response.status,
      message: error.message,
    });

    // Obtener la configuración original de la petición
    const originalRequest = error.config;

    // Si el error es 401 (Unauthorized) y no es una petición de renovación de token
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('refresh-token')
    ) {
      console.log('Error 401 detectado, intentando renovar token...');

      // Marcar la petición como reintentada
      originalRequest._retry = true;

      // Si ya hay una renovación en curso, esperar a que termine
      if (isRefreshing) {
        console.log('Renovación ya en curso, esperando...');
        return new Promise((resolve, _reject) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            console.log('Token renovado por otra petición, reintentando petición original');
            resolve(API(originalRequest));
          });
        });
      }

      // Iniciar renovación del token
      console.log('Iniciando renovación de token debido a error 401...');
      isRefreshing = true;
      try {
        const newToken = await refreshToken();
        console.log('Token renovado exitosamente, reintentando petición original');
        // Actualizar el token en la petición original
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        // Reintentar la petición original
        return API(originalRequest);
      } catch (refreshError) {
        console.error('Error al renovar token:', refreshError);
        // Si falla la renovación, rechazar la promesa
        return Promise.reject(refreshError);
      }
    }

    // Si estamos en la página de inicio y es una petición de datos básicos, simular respuesta exitosa
    if (
      window.location.pathname === '/' &&
      (originalRequest.url.includes('/matches') || originalRequest.url.includes('/categories'))
    ) {
      console.warn('Error en página de inicio interceptado:', error.message);
      return Promise.resolve({
        data: [],
        status: 200,
      });
    }

    // Si hay un error de conexión en cualquier página, devolver un array vacío para evitar que la aplicación se rompa
    if (
      error.message &&
      (error.message.includes('Network Error') || error.message.includes('timeout'))
    ) {
      console.warn('Error de conexión interceptado:', error.message);
      console.warn('URL de la petición:', originalRequest.url);

      // Si la petición es para obtener datos (GET), devolver un array vacío
      if (originalRequest.method === 'get') {
        return Promise.resolve({
          data: [],
          status: 200,
        });
      }
    }

    // Procesar el error usando nuestro manejador centralizado
    const customError = processApiError(error);

    // Manejar acciones específicas según el tipo de error
    if (customError.type === ErrorTypes.UNAUTHORIZED) {
      // Si no es una petición de renovación de token y ya se intentó renovar
      if (!originalRequest.url.includes('refresh-token') && originalRequest._retry) {
        // Limpiar datos de autenticación
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        localStorage.removeItem(LAST_ACTIVITY_KEY);

        // Redirigir al login solo si no estamos en la página de inicio o en una página pública
        if (
          !window.location.pathname.includes('/login') &&
          window.location.pathname !== '/' &&
          !window.location.pathname.includes('/about') &&
          !window.location.pathname.includes('/contact')
        ) {
          window.location.href = '/login?session=expired';
        }
      }
    } else if (customError.type === ErrorTypes.FORBIDDEN) {
      // Redirigir a la página de acceso denegado
      if (!window.location.pathname.includes('/unauthorized')) {
        window.location.href = '/unauthorized';
      }
    }

    // Registrar el error en la consola para depuración
    console.error(`API Error (${customError.type}):`, customError);

    // Devolver un objeto de error personalizado para que sea más fácil de manejar en los componentes
    return Promise.reject(customError);
  },
);

// No necesitamos exportar las funciones aquí porque ya las exportamos individualmente
export default API;
